import i18n from "i18next";
import { initReactI18next } from "react-i18next";

export const SUPPORTED_LANGUAGES = ["en", "fr", "es"];
export const DEFAULT_LANGUAGE = "en";
export const FALLBACK_LANGUAGE = "en";
export const LANGUAGE_STORAGE_KEY = "lang";
export const I18N_NAMESPACES = [
  "dashboard",
  "navigation",
  "layout",
  "sales",
  "invoice",
  "devis",
  "documents",
  "expenses",
  "customers",
  "comptabilite",
  "reports",
  "employees",
  "apps",
  "chat",
  "settings"
];

function createLocaleLoaders(language) {
  return Object.fromEntries(
    I18N_NAMESPACES.map((namespace) => [
      namespace,
      async () => (await import(`./locales/${language}/${namespace}.json`)).default
    ])
  );
}

const localeLoaders = {
  en: createLocaleLoaders("en"),
  fr: createLocaleLoaders("fr"),
  es: {
    settings: async () => (await import("./locales/es/settings.json")).default
  }
};

const backendPlugin = {
  type: "backend",
  read: async (language, namespace, callback) => {
    const normalizedLanguage = normalizeLanguage(language);
    const loader = localeLoaders[normalizedLanguage]?.[namespace];
    const fallbackLoader = localeLoaders[FALLBACK_LANGUAGE]?.[namespace];

    try {
      if (loader) {
        const resources = await loader();
        callback(null, resources);
        return;
      }

      if (fallbackLoader) {
        const resources = await fallbackLoader();
        callback(null, resources);
        return;
      }

      callback(new Error(`Missing translations for ${normalizedLanguage}/${namespace}`), true);
    } catch (error) {
      if (normalizedLanguage !== FALLBACK_LANGUAGE && fallbackLoader) {
        try {
          const resources = await fallbackLoader();
          callback(null, resources);
          return;
        } catch (fallbackError) {
          callback(fallbackError, true);
          return;
        }
      }

      callback(error, true);
    }
  }
};

export function normalizeLanguage(language) {
  const value = String(language || "").trim().toLowerCase();

  if (value.startsWith("fr")) {
    return "fr";
  }

  if (value.startsWith("es")) {
    return "es";
  }

  return "en";
}

export function getStoredLanguage() {
  if (typeof window === "undefined") {
    return null;
  }

  const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (!value) {
    return null;
  }

  return normalizeLanguage(value);
}

export function setStoredLanguage(language) {
  const normalizedLanguage = normalizeLanguage(language);

  if (typeof window !== "undefined") {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);
  }

  return normalizedLanguage;
}

export function detectBrowserLanguage() {
  if (typeof navigator === "undefined") {
    return DEFAULT_LANGUAGE;
  }

  return normalizeLanguage(navigator.language);
}

void i18n.use(backendPlugin).use(initReactI18next).init({
  lng: DEFAULT_LANGUAGE,
  fallbackLng: FALLBACK_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  defaultNS: "dashboard",
  ns: I18N_NAMESPACES,
  interpolation: {
    escapeValue: false
  }
});

export default i18n;
